import { useEffect, useState } from 'react';
import { FormRenderer } from '@atlas-forms/player-components-react';
import type { FormSchema } from '@atlas-forms/types-js';
import type { HilFormContentProps } from '@bizfirst/hil-ui';

export interface AtlasFormPresentationContentProps extends HilFormContentProps {
  /** Host's own API base URL — same host-configurable contract every shared package in this session follows (AiAgentCatalogApiClient, MediaSourceField, etc.), never assumed. */
  apiBaseUrl: string;
  /** Host's own token accessor. */
  getAuthToken: () => string | undefined;
  /** Dark-theme override for atlas-forms' FormRenderer, which ships only light-theme styling natively — same fix Flow Studio's HIL overlay already needed (see the CSS block below). Omit/false for light theme. */
  darkTheme?: boolean;
}

async function fetchAtlasFormSchema(
  apiBaseUrl: string,
  getAuthToken: () => string | undefined,
  formID: number
): Promise<{ schema: FormSchema | null; debugInfo: string }> {
  const url = `${apiBaseUrl.replace(/\/$/, '')}/api/v1/atlas/forms/by-id`;
  const token = getAuthToken();

  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ ID: { ID: formID } }),
  });

  const bodyText = await resp.text();
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status} for formID=${formID} | ${bodyText.substring(0, 120)}`);
  }

  let json: any;
  try { json = JSON.parse(bodyText); } catch { json = {}; }

  const rawSchema = json?.data?.schema ?? json?.data?.Schema ?? json?.Data?.schema ?? json?.Data?.Schema;
  if (!rawSchema) return { schema: null, debugInfo: `data=${JSON.stringify(json?.data).substring(0, 80)}` };
  const parsed = typeof rawSchema === 'string' ? JSON.parse(rawSchema) : rawSchema;
  return { schema: parsed, debugInfo: 'ok' };
}

/**
 * The one Atlas Forms-backed `HilFormContentRenderer` implementation in this codebase — wired into
 * `@bizfirst/hil-ui`'s `HilFormRendererProvider` by every host that shows HIL forms (Flow Studio's
 * WorkflowApp.tsx, and TemplateCard for App Player/WorkDesk — see ai-agent-catalog-ui's
 * HilPresentationPanel.tsx). Previously each host fetched/rendered the Atlas form schema itself
 * (Flow Studio's now-removed AtlasHilFormContent.tsx duplicated this exact fetch-and-render logic,
 * coupled to @flow-studio/api's apiConfig and @flow-studio/store's theme) — extracted here, host-
 * configurable via apiBaseUrl/getAuthToken/darkTheme, so there is exactly one implementation.
 */
export function AtlasFormPresentationContent({
  formID,
  prePopulate,
  onValuesChange,
  disabled,
  apiBaseUrl,
  getAuthToken,
  darkTheme,
}: AtlasFormPresentationContentProps) {
  const [formSchema, setFormSchema] = useState<FormSchema | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    setFormSchema(null);
    fetchAtlasFormSchema(apiBaseUrl, getAuthToken, formID)
      .then(({ schema, debugInfo }) => {
        if (schema) {
          setFormSchema(schema);
        } else {
          setLoadError(`Atlas form ${formID}: schema is empty (${debugInfo})`);
        }
      })
      .catch((e: Error) => setLoadError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formID, apiBaseUrl]);

  if (loading) {
    return <div style={styles.info}>Loading form…</div>;
  }

  if (loadError) {
    return <div style={styles.error}>{loadError}</div>;
  }

  if (!formSchema) return null;

  return (
    <div
      className={`hil-atlas-form${darkTheme ? ' hil-form-dark-renderer' : ''}`}
      style={{ pointerEvents: disabled ? 'none' : undefined, opacity: disabled ? 0.6 : undefined }}
    >
      {darkTheme && <style>{DARK_THEME_OVERRIDE_CSS}</style>}
      <FormRenderer schema={formSchema} initialValues={prePopulate} onValuesChange={onValuesChange} nested />
    </div>
  );
}

// atlas-forms' FormRenderer ships its own light-theme styling with no dark variant of its own —
// carried over verbatim from Flow Studio's now-removed AtlasHilFormContent.tsx, which needed this
// same override to avoid the form rendering light-on-light inside a dark-themed host.
const DARK_THEME_OVERRIDE_CSS = `
  .hil-form-dark-renderer input,
  .hil-form-dark-renderer select,
  .hil-form-dark-renderer textarea {
    background: #374151 !important;
    color: #e5e7eb !important;
    border-color: #4b5563 !important;
  }
  .hil-form-dark-renderer label,
  .hil-form-dark-renderer [class*="label"] {
    color: #d1d5db !important;
  }
  .hil-form-dark-renderer [class*="help"],
  .hil-form-dark-renderer [class*="hint"],
  .hil-form-dark-renderer [class*="description"] {
    color: #9ca3af !important;
  }
`;

const styles = {
  info: { padding: '12px 0', fontSize: 13, color: '#9ca3af' },
  error: { color: '#ef4444', fontSize: 13 },
} as const;
